import { useContext, useEffect, useRef, useState } from "react";
import { Modal } from "../ModalComponent/Modal";
import ModalDividerLine from "../ModalComponent/modal-divider-line";

import editIcon from "../../../assets/images/editPrimary.png";
import defaultProfilePicture from "../../../assets/images/defaultProfilePicture.png";

import "./edit-profile-picture.css";
import { UserContext } from "../../../context/UserContext";
import { LoaderContext } from "../../../context/LoaderContext";
import { API_URL } from "../../../enviormentVariables";
import { ChunkedWebsocketFileUpload, FormatFileName, POST, setAuth } from "../../../utils/httpUtils";
import ImageCropper from "./ImageCropper";
import { Alert, fallbackCopy, GetSessionUrls, isMobileDevice, Log, MbToBytes, RemoveSessionUrl, TrackSessionUrl } from "../../../utils/genericUtils";
import { AlertContext } from "../../../context/AlertContext";
import { NotificationsContext } from "../../../context/NotificationsContext";

import copy from "../../../assets/images/copy.png";

const defaultProps = {
  id: "id",
  isActive: false,
  header: "header",
  message: "message",
  headerFontSize: 25,
  messageFontSize: 15,
  icon: undefined,
  iconLeft: false,
  iconHeight: undefined,
  iconAsButton: false,
  onIconClick: () => {},
  textAlign: "left",
  showDivider: true,
  animate: true,
  animationTime: 300,
  bg_opacity: 0.5,
  height: undefined,
  width: undefined,
  children: null,
  onClickOut: () => {},
  onClickOut: () => {},
  onCloseModal: () => {},
  forceUpdate: () => {},
};

export function ProfilePictureModal(props = defaultProps) {
  const [editing, setEditing] = useState(false);
  const [hideEdit, setHideEdit] = useState(false);
  const [profilePic, setProfilePic] = useState(defaultProfilePicture);

  const { user, loginUser } = useContext(UserContext);
  const { show, showloader, hideloader } = useContext(LoaderContext);
  const { globalAlert } = useContext(AlertContext);
  const { pushNotification } = useContext(NotificationsContext);

  const [uploadedImage, setUploadedImage] = useState();

  const uploadRef = useRef(undefined);

  useEffect(() => {
    if (props.isActive === false && editing === true) {
      setEditing(false);
    }
    GetExistingProfilePicture();
  }, [props.isActive, uploadedImage]);

  function GetFile(objectId) {
    return new Promise((resolve) => {
      let request = new XMLHttpRequest();
      request.open("GET", API_URL + "/get-file/" + objectId);
      setAuth(request, user);
      request.responseType = "blob";
      request.send();
      request.onload = () => {
        if (request.status === 200) {
          // Get the response blob
          let blob = request.response;
          // Extract filename from response headers
          let fileName = request.getResponseHeader("x-fileName");

          //if file extension is .pdf
          let fileExtension = fileName.split(".").pop();

          if (fileExtension.toLowerCase() === "pdf") {
            // Ensure the correct MIME type
            blob = new Blob([blob], { type: "application/pdf" });
          }

          //if file extension is .doc
          if (fileExtension.toLowerCase() === "doc") {
            // Ensure the correct MIME type
            blob = new Blob([blob], { type: "application/msword" });
          }

          //if file extension is .doc
          if (fileExtension.toLowerCase() === "docx") {
            // Ensure the correct MIME type
            blob = new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
          }

          // Create a blob URL for the response blob
          let url = URL.createObjectURL(blob);
          return resolve({ fileName, url });
        } else {
          return resolve(null);
        }
      };
    });
  }

  function GetExistingProfilePicture() {
    if (uploadedImage !== undefined) {
      setProfilePic(uploadedImage);
    } else {
      let profile_picutre_session_url = GetSessionUrls().find((x) => x.objectId === "__profile_picture");

      if (profile_picutre_session_url) {
        setProfilePic(profile_picutre_session_url.url);
      } else {
        if (user && user.profilePictureUrl) {
          GetFile(user.profilePictureUrl).then((file) => {
            if (file && file.url) setProfilePic(file.url);
          });
        }
      }
    }
  }

  /**
   * set save button to active, open upload prompt
   */
  function onEditClicked() {
    setEditing(!editing);
    if (uploadRef.current) {
      uploadRef.current.click();
    }
  }

  function updatePreview(dataUrl) {
    setEditing(!editing);

    setUploadedImage(dataUrl);
  }

  async function RequestProfilePictureUpload(blobUrl) {
    return new Promise(async (resolve) => {
      try {
        // Fetch the blob from the URL
        let response = await fetch(blobUrl);
        let blob = await response.blob(); // Convert response to a blob
        let file = new File([blob], "profile-picture.png", { type: blob.type }); // Create a File object

        let fileReader = new FileReader();
        fileReader.onload = async (ev) => {
          let arrayBuffer = ev.target?.result;
          let fileBytes = arrayBuffer.byteLength;

          let uploadResponse = await POST("/request-upload", undefined, user, [
            { key: "x-file-name", value: "__profile_picture.png" },
            { key: "x-file-dir", value: "/" },
            { key: "x-file-size", value: fileBytes },
          ]);

          return resolve({ ...uploadResponse, arrayBuffer });
        };

        fileReader.readAsArrayBuffer(file);
      } catch (error) {
        return resolve({ success: false, error });
      }
    });
  }

  async function SaveProfilePicture() {
    if (isMobileDevice() && props.onClickOut) props.onClickOut();

    showloader();
    let upload_request = await RequestProfilePictureUpload(uploadedImage);

    if (!upload_request.success && upload_request.error) {
      Alert(globalAlert, "error", "Error", upload_request.error.message);
    }

    if (upload_request.success) {
      //stream file uplaod to websocket here
      let upload_websocket = upload_request.upload_websocket;

      await ChunkedWebsocketFileUpload(
        upload_websocket,
        upload_request.arrayBuffer,
        () => {
          showloader();
        },
        () => {
          //onProgress
        },
        //onFinished Uploading to Server
        () => {
          //Success Update image in side nav
          hideloader();
          RemoveSessionUrl("__profile_picture");
          TrackSessionUrl("__profile_picture", uploadedImage);
          //force a render in the side nav
          if (props.forceUpdate) props.forceUpdate();
        },
        //onClose
        () => {},
        (error) => {
          hideloader();
          Alert(globalAlert, "error", "Error", "Profile Picture Upload Failed: " + upload_websocket);
        },
        //1mb/s roughly
        MbToBytes(1)
      );
    }
  }

  function closeModal() {
    uploadRef.current.value = "";
    setHideEdit(false);
    setEditing(false);
    setUploadedImage(undefined);
    props.onCloseModal();
  }

  const publicUrl = window.location.origin + `/public/${user.id}`;

  return (
    <>
      <Modal id="edit-profile-picture-modal" {...props} animate={true} textAlign="center" header={"My Profile"} onClickOut={closeModal}>
        <section className="edit-profile-picture-modal">
          {/* profile pic */}
          {!hideEdit && (
            <>
              <div className="profile-picture-container">
                <img className="profile-picture" src={profilePic} onError={(e) => (e.target.src = defaultProfilePicture)} alt="" />
              </div>
            </>
          )}

          {/* hidden file upload */}
          <ImageCropper
            showLoader={showloader}
            hideLoader={hideloader}
            setHideEdit={setHideEdit}
            fileUploadRef={uploadRef}
            updateAvatar={updatePreview}
          />

          {/* divider */}
          <ModalDividerLine showDivider={true} />

          {/* edit button */}
          {!hideEdit && (
            <button className="edit-profile-pciture-button" onClick={onEditClicked}>
              change profile picture
              <img src={editIcon} alt="" />
            </button>
          )}

          {/* Public Url */}
          <div className="input-box">
            <label className="input-label" htmlFor="username-input">
              Share Profile Link
            </label>
            <div className="copy-input-container">
              {/* <input type="text" placeholder="" value={publicUrl} /> */}
              <button
                title="copy to clipboard"
                className="copy-url-button"
                onClick={() => {
                  try {
                    navigator.clipboard
                      .writeText(publicUrl)
                      .then(() => {
                        Log("Copied to clipboard:", publicUrl);
                        pushNotification("Copied to Clipboard", publicUrl);
                      })
                      .catch((err) => {
                        document.execCommand("copy", publicUrl);
                      });
                  } catch (error) {
                    fallbackCopy(publicUrl);
                    pushNotification("Copied to Clipboard", publicUrl);
                  }
                }}
              >
                <img src={copy} alt="" />
              </button>
            </div>
          </div>

          {/* controls */}
          <div className="modal-controls">
            <button className="standard-button" onClick={closeModal}>
              Close
            </button>
            <button className="standard-button" onClick={SaveProfilePicture} disabled={hideEdit}>
              Save
            </button>
          </div>
        </section>
      </Modal>
    </>
  );
}
